export function getAblyApiKey () {
  if (process.env.VUE_APP_ABLY_API_KEY) {
    return process.env.VUE_APP_ABLY_API_KEY;
  }

  if (typeof window !== "undefined") {
    const storedKey = window.localStorage ? window.localStorage.getItem("ably_api_key") : null;
    if (storedKey) {
      return storedKey;
    }
  }

  return null;
}

/**
 * Extracts subdomain from baseURL
 * Examples:
 *   https://dev2.biyo.co -> dev2
 *   https://myclient.example.com -> myclient
 *   http://localhost:8000 -> localhost_8000
 *   http://localhost:8080 -> localhost_8080
 * 
 * @param {string} baseUrl - The base URL (e.g., "https://dev2.biyo.co")
 * @returns {string} - The extracted subdomain (never falls back to "store")
 * @throws {Error} - If baseUrl is missing or cannot be parsed
 */
export function extractSubdomainFromBaseUrl (baseUrl) {
  if (!baseUrl) {
    throw new Error("[RealtimeConfig] baseUrl is required to extract subdomain");
  }

  try {
    // Parse the URL
    const url = new URL(baseUrl);
    const hostname = url.hostname;
    const port = url.port;
    
    // Split by dots to get parts
    const parts = hostname.split('.');
    
    // Handle localhost with port (e.g., localhost:8000 -> localhost_8000)
    if (hostname === 'localhost' && port) {
      return `localhost_${port}`;
    }
    
    // If we have at least 3 parts (e.g., dev2.biyo.co), the first is the subdomain
    if (parts.length >= 3) {
      // Return the first part (subdomain)
      return parts[0].toLowerCase();
    } 
    // If we have 2 parts, check if it's localhost or a domain without subdomain
    else if (parts.length === 2) {
      // If it's localhost, handle port if present
      if (parts[0] === 'localhost') {
        return port ? `localhost_${port}` : 'localhost';
      }
      // Otherwise, it's a domain without subdomain (e.g., example.com)
      // Extract the domain name itself as subdomain
      return parts[0].toLowerCase();
    } 
    // Single part (like localhost without domain, or IP address)
    else if (parts.length === 1) {
      // If it's localhost, handle port if present
      if (parts[0] === 'localhost') {
        return port ? `localhost_${port}` : 'localhost';
      }
      return parts[0].toLowerCase();
    }
    // Should not reach here, but throw error if we do
    else {
      throw new Error(`[RealtimeConfig] Unable to extract subdomain from hostname: ${hostname}`);
    }
  } catch (error) {
    // If it's already our error, re-throw it
    if (error.message && error.message.includes("[RealtimeConfig]")) {
      throw error;
    }
    // Otherwise, wrap the error
    throw new Error(`[RealtimeConfig] Failed to parse baseURL "${baseUrl}": ${error.message}`);
  }
}
